import { useState } from "react";
import { firebaseAuth } from "../../../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormTextInput from "../formInputs/FormTextInput";
import { useMiscStore } from "../../stateDefs";
import {
  sendPasswordResetRequest,
  sendVerificationEmailRequest,
} from "../../functions";

export default function SignUpForm(props: SignUpFormProps) {
  const [serverErrorCode, setServerErrorCode] = useState("");
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const setCompanyDetailsRequired = useMiscStore(
    (state) => state.setCompanyDetailsRequired,
  );

  const WRONG_PASSWORD_CODE = "auth/wrong-password";
  const USER_NOT_FOUND_CODE = "auth/user-not-found";
  const INVALID_CREDENTIAL_CODE = "auth/invalid-credential";
  const EMAIL_IN_USE_CODE = "auth/email-already-in-use";

  const handleSubmit = (
    { email, password }: { email: string; password: string },
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    if (props.mode === "forgot-password") {
      handleForgotPassword(email, setSubmitting);
      return;
    }
    if (props.mode === "signup") {
      handleSignup({ email, password }, setSubmitting);
      return;
    }
    signInWithEmailAndPassword(firebaseAuth, email, password)
      .then((userCredential) => {
        setSubmitting(false);
      })
      .catch((error) => {
        console.error(error);
        setServerErrorCode(error.code);
        setSubmitting(false);
      });
  };

  const formatServerError = () => {
    if (
      serverErrorCode === WRONG_PASSWORD_CODE ||
      serverErrorCode === USER_NOT_FOUND_CODE ||
      serverErrorCode === INVALID_CREDENTIAL_CODE
    ) {
      return "Incorrect email or password";
    }
    if (serverErrorCode === EMAIL_IN_USE_CODE) {
      return "An account with this email already exists. Please sign in instead.";
    }
    return "There was an error signing in. Please try again.";
  };

  const handleSignup = (
    { email, password }: { email: string; password: string },
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    createUserWithEmailAndPassword(firebaseAuth, email, password)
      .then(async (userCredential) => {
        try {
          await sendVerificationEmailRequest();
        } catch (error) {
          console.error("Error sending email verification:", error);
          // ignore the error, as it is not critical for signup:
          // the user can still request a verification email later
        }
        return userCredential;
      })
      .then(async (userCredential) => {
        // Signed in
        if (props.namePrefill) {
          await updateProfile(userCredential.user, {
            displayName: props.namePrefill,
          });
        }
        setCompanyDetailsRequired(true);
        return userCredential.user;
      })
      .catch((error) => {
        setServerErrorCode(error.code);
        setSubmitting(false);
      });
  };

  const handleForgotPassword = (
    email: string,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    setPasswordResetSent(false);
    // Our backend sends the email, so it carries Kono's name and links to a
    // Kono page. The answer is the same whether or not the address has an
    // account, so this screen cannot be used to discover who banks here.
    sendPasswordResetRequest(email)
      .then(() => {
        setPasswordResetSent(true);
        setServerErrorCode("");
      })
      .catch((error) => {
        console.error(error);
        setServerErrorCode(error.code || "reset-failed");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const submitLabel =
    props.mode === "signup"
      ? "Create account"
      : props.mode === "forgot-password"
        ? "Send reset link"
        : "Sign in";

  const serverErrorMessage =
    props.mode === "forgot-password"
      ? "Unable to send reset email. Please verify the address and try again."
      : formatServerError();

  return (
    <Formik
      initialValues={{
        email: props.emailPrefill || "",
        password: "",
      }}
      validationSchema={Yup.object({
        email: Yup.string()
          .email("Invalid email address")
          .required("Email is required"),
        ...(props.mode === "forgot-password"
          ? {}
          : {
              password: Yup.string()
                .min(8, "Must be at least 8 characters long")
                .required("Password is required"),
            }),
      })}
      onSubmit={(values, { setSubmitting }) =>
        handleSubmit(values, setSubmitting)
      }
    >
      {(formik) => (
        <Form>
          <FormTextInput label="Email" name="email" type="email" />

          {props.mode !== "forgot-password" && (
            <FormTextInput label="Password" name="password" type="password" />
          )}

          <button
            type="submit"
            className="py-2 bg-slate-800 block w-full rounded text-[#f1f5f9] disabled:bg-slate-600"
            disabled={formik.isSubmitting}
          >
            {submitLabel}
          </button>
          {passwordResetSent && (
            <div className="text-sm text-green-700 mt-3 min-h-[1.8em]">
              Password reset email sent. Please check your inbox.
            </div>
          )}
          <div className="text-sm text-[#b91c1c] mt-3 min-h-[1.8em]">
            {!!serverErrorCode && serverErrorMessage}
          </div>
        </Form>
      )}
    </Formik>
  );
}

type SignUpFormProps = {
  mode: "signin" | "signup" | "forgot-password";
  emailPrefill?: string;
  namePrefill?: string;
};
